import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

/**
 * Stops the browser's saved-info / saved-password suggestions on login screens.
 *
 * autocomplete="off" is not enough: Chromium (Edge, Chrome) ignores it and classifies a field
 * from its label and placeholder, so an "Email or Mobile No" box still gets the saved personal
 * info popup. A recognised token that is not an address field overrides that classification,
 * and the data-* flags do the same for the LastPass / Dashlane / 1Password extensions.
 *
 * Put it on the <form> of a login screen. Inputs that show up later - the OTP boxes, modals -
 * are stamped too. Mark an input with data-allow-autofill to leave it alone.
 */
@Directive({
  selector: '[appNoAutofill]'
})
export class NoAutofillDirective implements AfterViewInit, OnDestroy {

  private observer: MutationObserver;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) { }

  ngAfterViewInit() {
    this.stampInputs();

    // Only childList is watched, so writing the attributes below does not re-trigger this.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.stampInputs());
      this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });
    });
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private stampInputs() {
    const inputs = this.host.nativeElement.querySelectorAll<HTMLInputElement>(
      'input:not([data-allow-autofill]):not([data-no-autofill])'
    );

    inputs.forEach(input => {
      input.setAttribute('autocomplete', 'one-time-code');
      input.setAttribute('spellcheck', 'false');
      input.setAttribute('data-lpignore', 'true');
      input.setAttribute('data-form-type', 'other');
      input.setAttribute('data-1p-ignore', '');
      // Marks the input as done so the next mutation does not re-walk it.
      input.setAttribute('data-no-autofill', '');
    });
  }

}
